import type {AuthorizedFetch} from "./client";

export type UserAdministrationUser = {
  id: string;
  username: string;
  displayName: string;
  email: string;
  enabled: boolean;
  tenantId: string;
  company: string;
  businessUnit: string;
  roles: string[];
};

export type CreateUserRequest = {
  username: string;
  displayName: string;
  email: string;
  tenantId: string;
  company: string;
  businessUnit: string;
  roles: string[];
  initialPassword: string;
  temporaryPassword: boolean;
};

export type UpdateUserRequest = {
  displayName: string;
  email: string;
  tenantId: string;
  company: string;
  businessUnit: string;
};

export type UserAdministrationAudit = {
  id: string;
  action: string;
  outcome: string;
  actorUsername: string;
  targetUserId: string;
  targetUsername: string;
  occurredAt: string;
};

export const listUsers = (
  fetcher: AuthorizedFetch,
  signal?: AbortSignal,
): Promise<UserAdministrationUser[]> =>
  request<UserAdministrationUser[]>(
    fetcher,
    "/api/v1/administration/users",
    {signal},
  );

export const listAssignableRoles = (
  fetcher: AuthorizedFetch,
  signal?: AbortSignal,
): Promise<string[]> =>
  request<string[]>(
    fetcher,
    "/api/v1/administration/users/roles",
    {signal},
  );

export const createUser = (
  fetcher: AuthorizedFetch,
  value: CreateUserRequest,
): Promise<UserAdministrationUser> =>
  request<UserAdministrationUser>(
    fetcher,
    "/api/v1/administration/users",
    {
      method: "POST",
      body: JSON.stringify(value),
    },
  );

export const updateUser = (
  fetcher: AuthorizedFetch,
  id: string,
  value: UpdateUserRequest,
): Promise<UserAdministrationUser> =>
  request<UserAdministrationUser>(
    fetcher,
    `/api/v1/administration/users/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      body: JSON.stringify(value),
    },
  );

export const setUserEnabled = (
  fetcher: AuthorizedFetch,
  id: string,
  enabled: boolean,
): Promise<UserAdministrationUser> =>
  request<UserAdministrationUser>(
    fetcher,
    `/api/v1/administration/users/${encodeURIComponent(id)}/${enabled ? "enable" : "disable"}`,
    {method: "POST"},
  );

export const replaceRoles = (
  fetcher: AuthorizedFetch,
  id: string,
  roles: string[],
): Promise<UserAdministrationUser> =>
  request<UserAdministrationUser>(
    fetcher,
    `/api/v1/administration/users/${encodeURIComponent(id)}/roles`,
    {
      method: "PUT",
      body: JSON.stringify({roles}),
    },
  );

export const resetPassword = (
  fetcher: AuthorizedFetch,
  id: string,
  password: string,
  temporary: boolean,
): Promise<void> =>
  request<void>(
    fetcher,
    `/api/v1/administration/users/${encodeURIComponent(id)}/reset-password`,
    {
      method: "POST",
      body: JSON.stringify({password, temporary}),
    },
  );

export const listUserAudit = (
  fetcher: AuthorizedFetch,
  id: string,
  signal?: AbortSignal,
): Promise<UserAdministrationAudit[]> =>
  request<UserAdministrationAudit[]>(
    fetcher,
    `/api/v1/administration/users/${encodeURIComponent(id)}/audit`,
    {signal},
  );

async function request<T>(
  fetcher: AuthorizedFetch,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetcher(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init.body ? {"Content-Type": "application/json"} : {}),
      ...Object.fromEntries(new Headers(init.headers).entries()),
    },
  });

  if (!response.ok) {
    const problem = await response.json()
      .catch(() => null) as {message?: string} | null;

    throw new Error(
      problem?.message ??
      `User administration request failed: ${response.status}`,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return await response.json() as T;
}
